// reapply-enabled-tools-after-sync.ts
//
// Sau khi sync merge lại settings.json.tpl (có thể đổi/ghi đè settings), re-apply
// mọi tool đang enabled=true để hook tool KHÔNG bị mất. Không có bước này,
// tool "biến mất" sau mỗi `avatar sync`.
//
// #148: thêm auto-enable tool MỚI — tool available trong pack mà CHƯA có entry
// trong avatar-tools.json (chưa từng enable/disable) được TỰ BẬT khi sync, để
// rollout tool mới không phụ thuộc từng member gõ `avatar tools enable`.
// Semantics A: entry enabled:false = member chủ động tắt → GIỮ TẮT vĩnh viễn.
//
// Idempotent: enable lại không nhân đôi entry (dedupe trong enableTool lo).
// Fail-mềm: tool trong state nhưng pack đã bỏ manifest → enableToolByName
// trả false (warn), không crash sync.

import { listAvailableTools } from "./discover-pack-tools-and-defaults.js";
import { log } from "./terminal-logger.js";
import { enableToolByName } from "./tool-enable-disable-orchestrator.js";
import { readToolStateWithHealth } from "./tool-state-store.js";
import { SettingsLockTimeoutError } from "./with-settings-file-lock.js";

export async function reapplyEnabledToolsAfterSync(workspacePath: string): Promise<void> {
  const { state, corrupt } = await readToolStateWithHealth(workspacePath);
  const enabled = Object.entries(state.tools)
    .filter(([, entry]) => entry.enabled)
    .map(([name]) => name);
  if (enabled.length > 0) {
    log.info(`Re-apply ${enabled.length} tool(s) đang bật vào settings.json...`);
    // #164 R8/AC8 — try/catch PER-TOOL, bắt CHỈ SettingsLockTimeoutError.
    // Vòng này trước đây KHÔNG có try/catch (chỉ vòng tool MỚI ở dưới có). Lock là
    // fail-closed (chốt e) ⇒ 1 lần timeout ở tool ĐẦU sẽ thoát cả vòng, N−1 tool
    // không được re-apply, rồi sync.ts:206 nuốt thành log.warn mà VẪN báo sync thành
    // công ⇒ hook tool biến mất im lặng — đúng triệu chứng ticket #164.
    // ⛔ KHÔNG mở thành `catch (err)` bắt mọi lỗi: các throw sẵn có (hook path ngoài
    // workspace, settings.json unparseable) đang CỐ Ý nổi lên; nuốt chúng = đổi hành
    // vi fail-soft của đường đang chạy (vi phạm R5).
    const lockSkipped: string[] = [];
    for (const name of enabled) {
      try {
        await enableToolByName(workspacePath, name);
      } catch (err) {
        if (!(err instanceof SettingsLockTimeoutError)) throw err;
        lockSkipped.push(name);
        log.warn(
          `Tool '${name}': settings.json đang bị tiến trình khác giữ — BỎ QUA re-apply lần này.`,
        );
      }
    }
    // Dòng tổng kết: AC8 đòi phân biệt được "bỏ qua vì lock" với "thành công".
    // Vế lock chỉ in khi thực sự có tool bị bỏ (không thêm nhiễu cho ca bình thường).
    log.info(
      `Re-apply: ${enabled.length - lockSkipped.length} OK` +
        (lockSkipped.length > 0
          ? `, ${lockSkipped.length} bỏ qua vì lock (${lockSkipped.join(", ")})`
          : "") +
        ".",
    );
  }

  // Guard corrupt (#148): file TỒN TẠI nhưng parse fail ≠ state trống hợp lệ.
  // Coi corrupt là "0 entry" thì TOÀN BỘ available thành "mới" → mass auto-enable
  // (kể cả tool member đã tắt) + setToolState ghi đè file. → SKIP, giữ no-op như cũ.
  if (corrupt) {
    log.warn("avatar-tools.json hỏng — bỏ qua auto-enable tool mới. Sửa tay file rồi sync lại.");
    return;
  }

  // Tool MỚI = available trong pack nhưng KHÔNG có entry (kể cả enabled:false).
  const available = await listAvailableTools(workspacePath);
  const newTools = available.filter((n) => !(n in state.tools));
  if (newTools.length === 0) {
    return;
  }
  const failed: string[] = [];
  for (const name of newTools) {
    // try/catch per-tool: enableToolByName có nhánh THROW (hook path ngoài workspace,
    // settings.json unparseable) — 1 tool xấu không được chặn N-1 tool lành.
    // sync.ts vẫn còn catch tổng bên ngoài.
    try {
      const ok = await enableToolByName(workspacePath, name);
      if (ok) {
        log.info(
          `Tool MỚI trong pack: '${name}' — đã TỰ ENABLE (avatar tools disable ${name} để tắt).`,
        );
      } else {
        failed.push(name);
      }
    } catch (err) {
      log.warn(
        `Tool mới '${name}' enable lỗi: ${err instanceof Error ? err.message : String(err)}`,
      );
      failed.push(name);
    }
  }
  const okCount = newTools.length - failed.length;
  log.info(
    `Tool mới: ${okCount} auto-enabled${failed.length > 0 ? `, ${failed.length} enable-fail (${failed.join(", ")})` : ""}.`,
  );
}
